import { useEffect, ComponentProps } from 'react';
import { useTranslation } from 'react-i18next';
import { TransactionType, TransactionsLists } from 'vopay';
import EftWithdrawTransactionInfo from './EftWithdrawTransactionInfo';
import useFetchEFTWithdrawTransaction from '@/hooks/useApi/useFetchEFTWithdrawTransaction';
import { useUserSettingsProvider } from '@/providers/UserSettings.provider';
import { useBlockingLoader } from 'shared';
import { useAlertDialog } from '@/providers/AlertDialog.provider';

type EftWithdrawTransactionProps = {
  TransactionID: string;
  isParent?: boolean;
  // Resolved by TransactionDetails.tsx via a generic lookup by ID — shared with FundingStatusBanner
  // instead of this container fetching its own copy for the Credit Card fallback below.
  parentTransaction?: TransactionsLists;
} & Omit<
  ComponentProps<typeof EftWithdrawTransactionInfo>,
  'transaction' | 'isParent' | 'parentTransactionId' | 'parentTransactionStatus' | 'parentType'
>;

export default function EftWithdrawTransactionContainer({
  TransactionID,
  Notes,
  isParent = false,
  jwt,
  ELinxRequestID = '',
  parentTransaction,
  ...props
}: EftWithdrawTransactionProps) {
  const { userSettings } = useUserSettingsProvider();
  const blockingLoader = useBlockingLoader();
  const alertDialog = useAlertDialog();
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });

  const { eftWithdrawTransaction, fetchEftWithdrawTransaction } = useFetchEFTWithdrawTransaction({ bearer: jwt });

  useEffect(() => {
    blockingLoader.show();

    fetchEftWithdrawTransaction({ TransactionID, Timezone: userSettings.timezone })
      .catch((error) => {
        // Not returned: the dialog's promise settles on dismissal, and the loader must drop before that.
        alertDialog.error(tGlobal('error'), error instanceof Error ? error.message : tGlobal('errorOccurred'));
      })
      .finally(() => blockingLoader.hide());
  }, [TransactionID]);

  const transaction = eftWithdrawTransaction;

  const fundingParentTransaction = Object.values(transaction?.ParentTransaction || {}).find(
    (parent) => parent.TransactionType === TransactionType.EFT_FUNDING,
  );

  // The EFT Funding join above doesn't return a Credit Card parent (Credit Card funding isn't in
  // the table that join relies on), so fall back to the generically-resolved parent for that case only.
  const creditCardParent =
    !fundingParentTransaction && parentTransaction?.TransactionType === TransactionType.CREDIT_CARD
      ? parentTransaction
      : undefined;

  return (
    <div>
      {transaction && (
        <EftWithdrawTransactionInfo
          transaction={transaction}
          Notes={Notes}
          isParent={isParent}
          jwt={jwt}
          ELinxRequestID={ELinxRequestID}
          parentTransactionId={fundingParentTransaction?.TransactionID || creditCardParent?.TransactionID}
          parentTransactionStatus={fundingParentTransaction?.TransactionStatus || creditCardParent?.TransactionStatus}
          parentType={fundingParentTransaction ? 'EFT Funding' : creditCardParent ? 'Credit Card' : undefined}
          {...props}
        />
      )}
    </div>
  );
}
